import { DotNetBridge, IWritableSettings } from '@barthauer/abc/core';
import { addDays, differenceInSeconds } from 'date-fns';
import { AuthenticationLevelMode, IdentityMode, LoginInfo, PasswordRequirementsInfo } from './login-info.model';

export enum LoginSetting {
  access_token,
  refresh_token,
  token_type,
  expires,
  expires_in,
  issued,
  userName,
}

export interface ILoginSettings {
  readonly isAnonymousLogin: boolean;
  readonly access_token: string;
  readonly refresh_token: string;
  readonly token_type: string;
  readonly expires: Date;
  readonly expires_in: number;
  readonly issued: Date;
  readonly userName: string;
  readonly isExpired: boolean;
  readonly isLoginRequired: boolean;

  toLoginInfo(
    id?: string,
    name?: string,
    displayName?: string,
    isAdmin?: boolean,
    needsPasswordChange?: boolean,
    emailAddress?: string,
    secondFactorMode?: AuthenticationLevelMode,
    emailIdentityMode?: IdentityMode,
    phoneIdentityMode?: IdentityMode,
    appTotpIdentityMode?: IdentityMode,
    passwordRequirements?: PasswordRequirementsInfo
  ): LoginInfo;
}

export class LoginSettings implements ILoginSettings, IWritableSettings<ILoginSettings> {
  constructor(loginSettings?: ILoginSettings) {
    this.load(loginSettings);
  }

  private _isAnonymousLogin: boolean;
  public get isAnonymousLogin(): boolean {
    return this._isAnonymousLogin;
  }

  public access_token: string;
  public refresh_token: string;
  public token_type: string;
  public expires: Date;
  public expires_in: number;
  public issued: Date;
  public userName: string;

  public get isExpired(): boolean {
    return this.isLoginRequired ? this.expires < new Date() : false;
  }

  public get isLoginRequired(): boolean {
    return !this.isAnonymousLogin && !DotNetBridge.hasContext;
  }

  public static CreateAnonymousLoginSettings(): LoginSettings {
    const anonymousLoginSettings: LoginSettings = new LoginSettings();
    anonymousLoginSettings._isAnonymousLogin = true;
    anonymousLoginSettings.issued = new Date();
    anonymousLoginSettings.expires = addDays(anonymousLoginSettings.issued, 1);
    anonymousLoginSettings.expires_in = Math.abs(
      differenceInSeconds(anonymousLoginSettings.expires, anonymousLoginSettings.issued)
    );
    return anonymousLoginSettings;
  }

  public toLoginInfo(
    userId?: string,
    name?: string,
    displayName?: string,
    isAdmin?: boolean,
    needsPasswordChange?: boolean,
    emailAddress?: string,
    secondFactorMode?: AuthenticationLevelMode,
    emailIdentityMode?: IdentityMode,
    phoneIdentityMode?: IdentityMode,
    appTotpIdentityMode?: IdentityMode,
    passwordRequirements?: PasswordRequirementsInfo
  ): LoginInfo {
    return new LoginInfo(
      this.isAnonymousLogin,
      this.userName,
      this.issued,
      this.expires,
      userId,
      name,
      displayName,
      isAdmin,
      needsPasswordChange,
      emailAddress,
      secondFactorMode,
      emailIdentityMode,
      phoneIdentityMode,
      appTotpIdentityMode,
      passwordRequirements
    );
  }
  public load(loginSettings: ILoginSettings): void {
    if (loginSettings) {
      this._isAnonymousLogin = loginSettings.isAnonymousLogin;
      this.expires = loginSettings.expires;
      this.expires_in = loginSettings.expires_in;
      this.issued = loginSettings.issued;
      this.userName = loginSettings.userName;

      if (!loginSettings.isAnonymousLogin) {
        this.access_token = loginSettings.access_token;
        this.refresh_token = loginSettings.refresh_token;
        this.token_type = loginSettings.token_type;
      }
    } else {
      this._isAnonymousLogin = false;
    }
  }
}

export enum InputState {
  ready = 0,
  reset = 1,
}

export enum LoginViewMode {
  Login,
  SecondFactor,
  ChangePassword,
  TakeoverSession,
  LockoutInfo,
}

export class LoginViewState {
  private mode: LoginViewMode = LoginViewMode.Login;
  public readonly secondFactor = new SecondFactorViewState();

  public change(mode: LoginViewMode) {
    if (mode === LoginViewMode.SecondFactor) {
      throw new Error('Use changeSecondFactor function to switch to secondFactor view');
    }
    this.mode = mode;
  }

  public changeSecondFactor(mode: SecondFactorViewMode) {
    this.mode = LoginViewMode.SecondFactor;
    this.secondFactor.internalChange(mode);
  }

  public get showLogin(): boolean {
    return this.mode === LoginViewMode.Login;
  }

  public get showSecondFactor(): boolean {
    return this.mode === LoginViewMode.SecondFactor && this.secondFactor.enabled;
  }

  public get showChangePassword(): boolean {
    return this.mode === LoginViewMode.ChangePassword;
  }

  public get showTakeoverSession(): boolean {
    return this.mode === LoginViewMode.TakeoverSession;
  }

  public get showLockoutInfo(): boolean {
    return this.mode === LoginViewMode.LockoutInfo;
  }
}

export enum SecondFactorViewMode {
  None,
  InvalidConfig,
  ShouldSetup,
  Setup,
  SetupInvalidCode,
  SetupDeprecated,
  Input,
  InvalidCode,
}

export class SecondFactorViewState {
  private mode: SecondFactorViewMode = SecondFactorViewMode.None;

  public internalChange(mode: SecondFactorViewMode) {
    this.mode = mode;
  }

  public get enabled(): boolean {
    return this.mode !== SecondFactorViewMode.None;
  }

  public get showInput(): boolean {
    return this.isSetup || this.input || this.invalidCode;
  }

  public get showQRCode(): boolean {
    return this.isSetup;
  }

  private get isSetup(): boolean {
    return this.setup || this.setupDeprecated || this.setupInvalidCode;
  }

  public get showInvalidCodeMessage(): boolean {
    return this.invalidCode || this.setupInvalidCode;
  }

  public get invalidConfig(): boolean {
    return this.mode === SecondFactorViewMode.InvalidConfig;
  }

  public get shouldSetup(): boolean {
    return this.mode === SecondFactorViewMode.ShouldSetup;
  }

  public get setup(): boolean {
    return this.mode === SecondFactorViewMode.Setup;
  }

  public get setupInvalidCode(): boolean {
    return this.mode === SecondFactorViewMode.SetupInvalidCode;
  }

  public get setupDeprecated(): boolean {
    return this.mode === SecondFactorViewMode.SetupDeprecated;
  }

  public get input(): boolean {
    return this.mode === SecondFactorViewMode.Input;
  }

  public get invalidCode(): boolean {
    return this.mode === SecondFactorViewMode.InvalidCode;
  }
}
